import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { ItemsTable } from '@/components/form/ItemsTable';
import { listParties } from '@/api/parties';
import { listItems } from '@/api/items';
import { useAuth } from '@/auth/useAuth';
import { formatINR } from '@/lib/format';
import {
  ENQ_CATEGORIES, ENQ_CHANNELS, REP_SETTABLE_STATUSES, uid, enqLineAmount, summarizeItems,
  type Enquiry, type EnqItem,
} from './enquiryDomain';

interface Props {
  open: boolean;
  editing: Enquiry | null;
  people: string[];
  /** Pre-seed a new enquiry's items (e.g. from the catalogue cart). */
  presetItems?: EnqItem[];
  onClose: () => void;
  onSave: (row: Enquiry) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

function blank(people: string[], defaultAssignee = ''): Enquiry {
  return {
    id: '', date: today(), partyId: null, customer: '', category: ENQ_CATEGORIES[0], channel: ENQ_CHANNELS[0],
    // Default a new enquiry's owner to the logged-in user.
    assignedTo: defaultAssignee || people[0] || '', items: [], item: '', qty: 0, status: REP_SETTABLE_STATUSES[0], valueInr: 0,
    salesOrderNo: '', approvedBy: '', notes: '', comms: [],
  };
}

/** Legacy enquiries (created before the items table) → seed a single row from
 *  the old summary fields so they open cleanly in the table. */
function withItems(e: Enquiry): Enquiry {
  if (e.items?.length) return e;
  const seed: EnqItem[] = e.item
    ? [{ id: uid(), itemId: null, item: e.item, qty: e.qty || 0, unit: '', rate: e.qty ? (e.valueInr || 0) / e.qty : (e.valueInr || 0) }]
    : [];
  return { ...e, items: seed };
}

const emptyLine = (): EnqItem => ({ id: uid(), itemId: null, item: '', qty: 0, unit: '', rate: 0 });

export function EnquiryFormModal({ open, editing, people, presetItems, onClose, onSave }: Props) {
  const { user } = useAuth();
  const [f, setF] = useState<Enquiry>(blank(people));
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!open) return;
    const me = user?.fullName ?? '';
    if (editing) {
      const withRows = withItems({ ...editing });
      // Keep an existing owner; auto-pick the logged-in user when unassigned.
      setF({ ...withRows, assignedTo: withRows.assignedTo?.trim() ? withRows.assignedTo : me });
    } else {
      const b = blank(people, me);
      // Pre-seed items from the cart when arriving via the catalogue.
      setF(presetItems?.length ? { ...b, items: presetItems } : b);
    }
    setErr(false);
  }, [open, editing, people, user, presetItems]);

  // Existing customers come from Party Master (customers + converted leads).
  const { data: customersResp } = useQuery({
    queryKey: ['parties', { type: 'customer', includeLeads: true, pageSize: 10000 }],
    queryFn: () => listParties({ type: 'customer', includeLeads: true, pageSize: 10000 }),
    enabled: open,
    staleTime: 60_000,
  });
  // Options are keyed by party id (unique) — party names can repeat in Party
  // Master, so a name-keyed option list both collides React keys and would
  // resolve the wrong partyId on select.
  const customerOptions: SearchableOption[] = useMemo(() => {
    const opts = (customersResp?.data ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      sublabel: [c.code, c.city, c.mobile].filter(Boolean).join(' · '),
    }));
    if (f.partyId && !opts.some((o) => o.value === f.partyId)) {
      opts.unshift({ value: f.partyId, label: f.customer || `Party #${f.partyId}`, sublabel: 'current selection' });
    }
    return opts;
  }, [customersResp, f.partyId, f.customer]);

  // Every item from Item Master (complete list), grouped by category in the
  // per-row picker below.
  const { data: itemsResp } = useQuery({
    queryKey: ['items', { pageSize: 10000, forEnquiryPicker: true }],
    queryFn: () => listItems({ pageSize: 10000 }),
    enabled: open,
    staleTime: 60_000,
  });
  const masterItems = useMemo(() => itemsResp?.data ?? [], [itemsResp]);
  const pickerItems = useMemo(
    () => masterItems.map((it) => {
      const w = it.width != null ? Number(it.width) : null;
      const l = it.length != null ? Number(it.length) : null;
      return {
        id: it.id,
        code: it.code,
        name: it.name,
        category: it.category,
        currentStock: Number(it.warehouseStock?.currentQty ?? 0),
        barcode: it.barcode,
        color: it.color,
        size: w != null && l != null && (w || l) ? `${w}x${l}` : null,
        brand: it.brand,
        style: it.style,
        collection: it.collection,
      };
    }),
    [masterItems],
  );

  const total = useMemo(() => f.items.reduce((s, l) => s + enqLineAmount(l), 0), [f.items]);

  // Once the enquiry has entered the operational pipeline (stage past 'new'),
  // its status and sales-order number are owned by ops and shown read-only —
  // the dashboard mirrors the pipeline, it doesn't drive it.
  const opsControlled = !!f.stage && f.stage !== 'new';

  function save() {
    // A dashboard enquiry must be tied to an existing Party customer (partyId).
    if (!f.partyId) { setErr(true); return; }
    // Drop fully-empty rows, then roll the table up into the summary fields.
    const items = f.items.filter((l) => l.item.trim() || l.qty || l.rate);
    onSave({
      ...f,
      id: f.id || uid(),
      customer: f.customer.trim(),
      salesOrderNo: f.salesOrderNo.trim(),
      approvedBy: f.approvedBy.trim(),
      items,
      ...summarizeItems(items),
    });
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit enquiry' : 'Log a new enquiry'}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save}>Save enquiry</Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormGroup label="Date">
          <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        </FormGroup>
        <FormGroup label="Category">
          <Select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value as Enquiry['category'] })}>
            {ENQ_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </FormGroup>

        <FormGroup label="Customer (existing)" required error={err ? 'Select an existing customer from Party Master' : null}>
          <SearchableSelect
            options={customerOptions}
            value={f.partyId ?? ''}
            onChange={(v) => {
              const id = v === '' ? null : Number(v);
              const c = (customersResp?.data ?? []).find((x) => x.id === id);
              setF({ ...f, partyId: id, customer: c ? c.name : '' });
              setErr(false);
            }}
            placeholder="Search a customer from Party Master…"
          />
        </FormGroup>

        <FormGroup label="Channel">
          <Select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value as Enquiry['channel'] })}>
            {ENQ_CHANNELS.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </FormGroup>
        {/* Owner is fixed to the logged-in user and can't be changed here. */}
        <FormGroup label="Entered by" hint="Set to you automatically; can't be changed once saved.">
          <Input value={f.assignedTo || '— Unassigned —'} readOnly disabled />
        </FormGroup>

        <FormGroup label="Status" hint={opsControlled ? 'Set by the sales pipeline (Order Review / Dispatch Order).' : undefined}>
          {opsControlled ? (
            <Input value={f.status} readOnly disabled />
          ) : (
            <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as Enquiry['status'] })}>
              {REP_SETTABLE_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </Select>
          )}
        </FormGroup>
        <FormGroup label="Approved by (Sales Head)">
          <Input value={f.approvedBy} onChange={(e) => setF({ ...f, approvedBy: e.target.value })} placeholder="e.g. Sales Head name" />
        </FormGroup>

        <FormGroup label="Sales Order number" hint={opsControlled ? 'From the linked Dispatch Order.' : 'from ERP, once stock confirmed'}>
          <Input
            value={f.salesOrderNo}
            onChange={(e) => setF({ ...f, salesOrderNo: e.target.value })}
            placeholder="e.g. SO-1042"
            readOnly={opsControlled}
            disabled={opsControlled}
          />
        </FormGroup>

        <FormGroup label="Notes" className="sm:col-span-2">
          <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </FormGroup>

        {/* Items table — pick from Item Master (grouped by category) or type a
            custom line. Amount = Qty × Rate; total is the Est. Order Value. */}
        <div className="sm:col-span-2">
          <ItemsTable<EnqItem>
            title="Items"
            rows={f.items}
            onChange={(items) => setF({ ...f, items })}
            newRow={emptyLine}
            readOnly={opsControlled}
            totalLabel="Est. Order Value"
            totalValue={`₹${formatINR(total)}`}
            columns={[
              {
                key: 'picker',
                header: 'Item',
                cell: (row, _i, update) => (
                  <CategoryItemPicker
                    items={pickerItems}
                    value={row.itemId ?? '__custom__'}
                    disabled={opsControlled}
                    onCustom={() => update({ itemId: null })}
                    onPick={(id) => {
                      const it = masterItems.find((m) => m.id === id);
                      if (it) update({ itemId: it.id, item: it.name, unit: it.unit, rate: Number(it.mrp ?? 0) });
                    }}
                  />
                ),
              },
              {
                key: 'item',
                header: 'Description',
                cell: (row, _i, update) => (
                  <Input value={row.item} onChange={(e) => update({ item: e.target.value })} placeholder="e.g. 8x10 hand-tufted wool rug" disabled={opsControlled} />
                ),
              },
              {
                key: 'qty',
                header: 'Qty',
                width: '80px',
                align: 'right',
                cell: (row, _i, update) => (
                  <Input type="number" value={row.qty || ''} onChange={(e) => update({ qty: Number(e.target.value) })} className="text-right" placeholder="0" disabled={opsControlled} />
                ),
              },
              {
                key: 'unit',
                header: 'Unit',
                width: '80px',
                cell: (row, _i, update) => (
                  <Input value={row.unit} onChange={(e) => update({ unit: e.target.value })} placeholder="Pcs" disabled={opsControlled} />
                ),
              },
              {
                key: 'rate',
                header: 'Rate (₹)',
                width: '110px',
                align: 'right',
                cell: (row, _i, update) => (
                  <Input type="number" value={row.rate || ''} onChange={(e) => update({ rate: Number(e.target.value) })} className="text-right" placeholder="0" disabled={opsControlled} />
                ),
              },
              {
                key: 'amount',
                header: 'Amount',
                width: '120px',
                align: 'right',
                cell: (row) => (
                  <span className="block w-full text-right font-bold text-accent">₹{formatINR(enqLineAmount(row))}</span>
                ),
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
}
